import SmartCache from '../utils/cache';
import apiMonitor from '../utils/apiMonitor';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Mock redisAvailable for frontend - always returns false since frontend doesn't use Redis
const redisAvailable = () => false;

class DashboardService {
  constructor() {
    this.cache = new SmartCache();
    this.abortControllers = new Map(); // Store controllers by request type
    this.requestStatus = new Map(); // Track request status with TTL
    this.activePromises = new Map(); // Store active promises for deduplication
  }

  /**
   * Check if cached data is still valid, with background refresh for stale data
   */
  async checkCacheAndRefresh(cacheKey) {
    if (!redisAvailable()) {
      return null;
    }
    
    let cached;
    try {
      cached = this.cache.get(cacheKey);
    } catch (error) {
      console.debug('Cache get error:', error.message);
      return null;
    }
    
    if (cached) {
      // If data is stale but still valid, trigger background refresh
      if (this.cache.isStale(cacheKey)) {
        // Background refresh without waiting
        this.backgroundRefresh(cacheKey).catch(error => {
          console.warn('Background refresh failed:', error);
        });
      }
      return cached;
    }
    
    return null;
  }

  /**
   * Background refresh for stale data
   */
  async backgroundRefresh(cacheKey) {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      let endpoint;
      if (cacheKey.includes('recent-activity')) {
        endpoint = '/dashboard/recent-activity';
      } else if (cacheKey.includes('statistics')) {
        endpoint = '/dashboard/statistics';
      } else {
        return; // Unknown cache key
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // For statistics endpoint, extract the statistics data from standardized response
        const cacheData = endpoint === '/dashboard/statistics' ? 
          (data.data?.statistics || data.statistics || data.data || data) : data;
        if (redisAvailable()) {
          try {
            this.cache.set(cacheKey, cacheData);
          } catch (error) {
            console.debug('Background cache set error:', error.message);
          }
        }
      }
    } catch (error) {
      // Silently fail background refresh
      console.warn('Background refresh failed:', error);
    }
  }

  /**
   * Cancel ongoing request of specific type
   */
  cancelOngoingRequest(requestType = 'recent-activity') {
    const controller = this.abortControllers.get(requestType);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(requestType);
    }
  }

  /**
   * Check if request is already in progress with TTL
   */
  isRequestInProgress(requestType) {
    const status = this.requestStatus.get(requestType);
    if (status && Date.now() - status.timestamp < 5000) { // 5 second TTL
      return true;
    }
    // Clean up expired status
    if (status) {
      this.requestStatus.delete(requestType);
    }
    return false;
  }

  /**
   * Mark request as in progress
   */
  markRequestInProgress(requestType) {
    this.requestStatus.set(requestType, { timestamp: Date.now() });
  }

  /**
   * Mark request as completed
   */
  markRequestCompleted(requestType) {
    this.requestStatus.delete(requestType);
  }

  /**
   * Direct fetch for statistics without caching (when Redis is unavailable)
   */
  async _fetchStatisticsDirectly() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/dashboard/statistics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Handle standardized response format { data: { statistics: {...} } }
      return data.data?.statistics || data.statistics || data.data || data;
    } catch (error) {
      console.error('Direct statistics fetch error:', error);
      throw error;
    }
  }

  /**
   * Direct fetch for recent activity without caching (when Redis is unavailable)
   */
  async _fetchRecentActivityDirectly() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }


      const response = await fetch(`${API_BASE_URL}/dashboard/recent-activity`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });


      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('📋 [DashboardService] Recent activity API error:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('📋 [DashboardService] Direct recent activity fetch error:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      throw error;
    }
  }

  /**
   * Get recent activity for the dashboard with strict deduplication
   */
  async getRecentActivity() {
    
    const requestType = 'recent-activity';
    
    // Strict deduplication: return existing promise if one exists
    if (this.activePromises.has(requestType)) {
      return this.activePromises.get(requestType);
    }
    
    // Simplified approach: if Redis is not available, fetch directly
    if (!redisAvailable()) {
      const promise = this._fetchRecentActivityDirectly();
      this.activePromises.set(requestType, promise);
      
      // Clean up promise when done
      promise.finally(() => {
        this.activePromises.delete(requestType);
      });
      
      return promise;
    }
    
    const cacheKey = this.getCacheKey(requestType);
    
    // Check cache first
    let cachedData;
    try {
      cachedData = this.cache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    } catch (error) {
    }

    // Create and store the promise for deduplication
    const requestPromise = this._executeRecentActivityRequest(requestType, cacheKey);
    this.activePromises.set(requestType, requestPromise);
    
    // Clean up the promise when it completes
    requestPromise.finally(() => {
      this.activePromises.delete(requestType);
    });
    
    return requestPromise;
  }

  /**
   * Get dashboard badges from consolidated statistics endpoint
   */
  async getBadges() {
    try {
      const statistics = await this.getStatistics();
      return statistics?.badges || {};
    } catch (error) {
      console.error('Error fetching badges from statistics:', error);
      throw new Error('Failed to fetch dashboard badges');
    }
  }

  /**
   * Get SLA metrics from consolidated statistics endpoint
   */
  async getSlaMetrics() {
    try {
      const statistics = await this.getStatistics();
      return statistics?.metrics || {};
    } catch (error) {
      console.error('Error fetching SLA metrics from statistics:', error);
      throw new Error('Failed to fetch SLA metrics');
    }
  }

  /**
   * Get claim counts from consolidated statistics endpoint
   */
  async getClaimCounts() {
    try {
      const statistics = await this.getStatistics();
      return statistics?.counts || {};
    } catch (error) {
      console.error('Error fetching claim counts from statistics:', error);
      throw new Error('Failed to fetch claim counts');
    }
  }

  /**
   * Get consolidated dashboard statistics
   */
  async getStatistics() {
    
    // If Redis is not available, skip caching and fetch directly
    if (!redisAvailable()) {
      return this._fetchStatisticsDirectly();
    }
    
    const requestType = 'statistics';
    const cacheKey = this.getCacheKey(requestType);
    
    // Check if there's already an active promise for this request type
    if (this.activePromises.has(requestType)) {
      console.log('Returning existing promise for statistics');
      return this.activePromises.get(requestType);
    }
    
    // Check if request is already in progress with TTL check
    if (this.isRequestInProgress(requestType)) {
      console.warn('Statistics request already in progress, skipping');
      try {
        return this.cache.get(cacheKey) || null;
      } catch (error) {
        console.debug('Cache get error:', error.message);
        return null;
      }
    }
    
    // Check cache and handle background refresh for stale data
    let cachedData;
    try {
      cachedData = await this.checkCacheAndRefresh(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    } catch (error) {
      console.debug('Cache check error:', error.message);
    }

    // Only cancel ongoing requests if they're older than 2 seconds (stale)
    const existingController = this.abortControllers.get(requestType);
    if (existingController) {
      const status = this.requestStatus.get(requestType);
      if (status && Date.now() - status.timestamp > 2000) {
        // Only cancel if request is older than 2 seconds
        this.cancelOngoingRequest(requestType);
      } else {
        // Request is fresh, don't start a new one
        console.warn('Fresh request already in progress, skipping');
        return this.cache.get(cacheKey) || null;
      }
    }
    
    // Create and store the promise for deduplication
    const requestPromise = this._executeStatisticsRequest(requestType, cacheKey);
    this.activePromises.set(requestType, requestPromise);
    
    // Clean up the promise when it completes
    requestPromise.finally(() => {
      this.activePromises.delete(requestType);
    });
    
    return requestPromise;
  }

  /**
   * Execute the actual statistics request
   */
  async _executeStatisticsRequest(requestType, cacheKey) {
    // Mark request as in progress with TTL
    this.markRequestInProgress(requestType);

    // Create new abort controller for this request type
    const controller = new AbortController();
    this.abortControllers.set(requestType, controller);

    // Set up 30-second timeout (increased from 10 seconds for slow queries)
    const timeoutId = setTimeout(() => {
      console.warn('📈 [DashboardService] Statistics request timed out after 30 seconds');
      controller.abort();
      this.abortControllers.delete(requestType);
    }, 30000);

    let lastError;
    
    // Retry logic: 3 attempts with exponential backoff
    for (let attempt = 1; attempt <= 3; attempt++) {
      let requestId;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Start monitoring the request
        requestId = apiMonitor.startRequest('/dashboard/statistics', 'GET');

        const response = await fetch(`${API_BASE_URL}/dashboard/statistics`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
          apiMonitor.endRequest(requestId, false, error);
          throw error;
        }

        const data = await response.json();
        
        // End monitoring successfully
        apiMonitor.endRequest(requestId, true);
        
        // Cache the response - extract statistics from standardized response format
        // Backend returns { data: { statistics: {...} } }
        const statistics = data.data?.statistics || data.statistics || data.data || data;
        if (redisAvailable()) {
          try {
            this.cache.set(cacheKey, statistics);
          } catch (error) {
            console.debug('Cache set error:', error.message);
          }
        }
        
        // Clean up
        this.abortControllers.delete(requestType);
        this.markRequestCompleted(requestType);
        
        return statistics;
      } catch (error) {
        lastError = error;
        
        // End monitoring with error
        if (requestId) {
          apiMonitor.endRequest(requestId, false, error);
        }
        
        // If this is an abort error, break out of retry loop
        if (error.name === 'AbortError') {
          break;
        }
        
        // Clean up on each attempt
        clearTimeout(timeoutId);
        this.abortControllers.delete(requestType);
        
        // Only retry on network errors or 5xx status codes
        if (attempt === 3 || (error.message.includes('NetworkError') === false && !error.message.includes('status: 5'))) {
          break;
        }
        
        // Exponential backoff: wait 2^attempt * 100ms before retrying
        const delay = Math.pow(2, attempt) * 100;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // Clean up
    clearTimeout(timeoutId);
    this.abortControllers.delete(requestType);
    this.markRequestCompleted(requestType);
    
    // If all retries failed, throw the last error
    throw lastError || new Error('Failed to fetch statistics after multiple attempts');
  }

  /**
   * Execute the actual recent activity request with simplified retry logic
   */
  async _executeRecentActivityRequest(requestType, cacheKey) {
    
    // Mark request as in progress
    this.markRequestInProgress(requestType);

    // Create new abort controller for this request type
    const controller = new AbortController();
    this.abortControllers.set(requestType, controller);

    // Set up 8-second timeout (reduced from 10)
    const timeoutId = setTimeout(() => {
      console.warn('⏰ [DashboardService._executeRecentActivityRequest] Request timeout after 8 seconds');
      controller.abort();
      this.abortControllers.delete(requestType);
    }, 8000);

    let lastError;
    
    // Simplified retry logic: 2 attempts instead of 3
    for (let attempt = 1; attempt <= 2; attempt++) {
      let requestId;
      try {
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Start monitoring the request
        requestId = apiMonitor.startRequest('/dashboard/recent-activity', 'GET');

        const response = await fetch(`${API_BASE_URL}/dashboard/recent-activity`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
          apiMonitor.endRequest(requestId, false, error);
          throw error;
        }

        const data = await response.json();
        

        // End monitoring
        apiMonitor.endRequest(requestId, true);

        // Cache the result
        try {
          this.cache.set(cacheKey, data, this.CACHE_TTL);
        } catch (cacheError) {
          console.debug('💾 [DashboardService._executeRecentActivityRequest] Cache set error:', cacheError.message);
        }

        // Clean up
        this.abortControllers.delete(requestType);
        this.clearRequestInProgress(requestType);

        return data;

      } catch (error) {
        lastError = error;
        console.error(`❌ [DashboardService._executeRecentActivityRequest] Attempt ${attempt} failed:`, error.message);
        
        if (requestId) {
          apiMonitor.endRequest(requestId, false, error);
        }
        
        // Don't retry for auth errors or abort errors
        if (error.message.includes('401') || error.message.includes('403') || 
            error.name === 'AbortError' || error.message.includes('aborted')) {
          break;
        }
        
        // Wait before retry (only if not the last attempt)
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // Clean up on failure
    clearTimeout(timeoutId);
    this.abortControllers.delete(requestType);
    this.clearRequestInProgress(requestType);

    console.error('❌ [DashboardService._executeRecentActivityRequest] All attempts failed');
    throw lastError || new Error('Failed to fetch recent activity after multiple attempts');
  }

  /**
   * Clear request in progress status
   */
  clearRequestInProgress(requestType) {
    this.requestStatus.delete(requestType);
  }

  /**
   * Invalidate cache for recent activity (call when claims are created/updated)
   */
  invalidateCache() {
    this.cache.invalidatePattern('dashboard');
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats() {
    return this.cache.getStats();
  }

  /**
   * Generate cache key for request deduplication
   */
  getCacheKey(endpoint) {
    return `dashboard_${endpoint}`;
  }
}

// Export singleton instance
export default new DashboardService();
